// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";

import {
  CreateRefMemberProcedureExamination,
  GetRefMemberProcedureExaminationDetailByID,
  UpdateRefMemberProcedureExamination,
} from "../../../services/RefMemberProcedureExamination";

// SCHEMA FOR FORM
interface formInitialValueTypes {
  refMemberProcedureExaminationId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  departmentId: number;
  specialityId: number;
  examinationName: string;
  code: string;
  isLocked: boolean;
  isDeleted: boolean;
}

export default function RefMemberProcedureExaminationSave(props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name } = props;

  // DEFAULT SCHEMA VALUE FOR FORM

  const initlRefMemberProcedureExaminationData: formInitialValueTypes = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    departmentId: 0,
    specialityId: 0,
    isDeleted: false,
    refMemberProcedureExaminationId: 0,
    examinationName: isForPopUp ? name : "",
    isLocked: false,
    code: "",
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();

  let curRawRefMemberProcedureExaminationId = urlParams.get("q");
  let curRefMemberProcedureExaminationId = 0;
  if (
    curRawRefMemberProcedureExaminationId !== null &&
    curRawRefMemberProcedureExaminationId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawRefMemberProcedureExaminationId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curRefMemberProcedureExaminationId = +decId;
    }
  } else {
    // if (urlParams.size > 0)
    //   navigate("/not-found");
  }

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    refMemberProcedureExaminationId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    organizationId: Yup.number().required(),
    branchId: Yup.number().required(),
    departmentId: Yup.number().required(),
    specialityId: Yup.number().required(),
    examinationName: Yup.string().required("Examination is required"),
    code: Yup.string(),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initlRefMemberProcedureExaminationData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // // GET ref Patient Food Item  DETAIL
  const getRefMemberProcedureExaminationDetail = async () => {
    let DentalExamination: any;
    let params = new URLSearchParams();
    params.append(
      "refMemberProcedureExaminationId",
      curRefMemberProcedureExaminationId?.toString(),
    );
    params.append("consumerId", userContext?.consumerId.toString()); // TEMP ONLY REMOVE AFTER PIN FUNCTIONALITY COMPLETE
    params.append("organizationId", userContext?.organizationId.toString());
    params.append("branchId", userContext?.branchId.toString());
    params.append("departmentId", userContext?.departmentId.toString());
    params.append("specialityId", userContext?.specialityId.toString());
    try {
      const res: any = await GetRefMemberProcedureExaminationDetailByID(params);
      DentalExamination = res.data.data;
      methods.reset({
        ...initlRefMemberProcedureExaminationData,
        refMemberProcedureExaminationId:
          DentalExamination?.refMemberProcedureExaminationId || 0,
        code: DentalExamination?.code || "",
        examinationName: DentalExamination?.examinationName || "",
      });
      return DentalExamination;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE RefHistoryDentalExamination
  const saveRefMemberProcedureExamination = async (
    value: formInitialValueTypes,
  ) => {
    try {
      let result: any = "";
      let formData = {
        refMemberProcedureExaminationId: value.refMemberProcedureExaminationId,
        specialityId: userContext?.specialityId,
        departmentId: userContext?.departmentId,
        branchId: userContext?.branchId,
        organizationId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        examinationName: value.examinationName?.trim(),
        code: value.code?.trim(),
        isLocked: value.isLocked,
        isDeleted: value.isDeleted,
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curRefMemberProcedureExaminationId > 0) {
        result = await UpdateRefMemberProcedureExamination(formData);
      } else {
        result = await CreateRefMemberProcedureExamination(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp("EXAMINATION_CREATE", {
              refMemberProcedureExaminationId:
                result?.data?.refMemberProcedureExaminationId,
              examinationName: result?.data?.examinationName,
            });
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveRefMemberProcedureExamination(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curRefMemberProcedureExaminationId > 0) {
        await getRefMemberProcedureExaminationDetail();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curRefMemberProcedureExaminationId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  {/* <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth>
                                            <FormLabel
                                                className={`fileds-label-text  ${methods.formState.errors.code
                                                    ? "validation-message"
                                                    : ""
                                                    }`}
                                            >
                                                Code
                                            </FormLabel>
                                            <SkeletonWrapper
                                                isLoading={showSkeleton}
                                                className="skeleton-input"
                                            >
                                                <CustomTextField
                                                    placeholder=""
                                                    autoCapitalize="none"
                                                    type="text"
                                                    fullWidth
                                                    size="small"
                                                    {...methods.register("code")}
                                                    error={!!methods.formState.errors.code}
                                                    className={`text-input ${methods.formState.errors.code ? "error" : ""
                                                        }`}
                                                />
                                            </SkeletonWrapper>
                                        </FormControl>
                                        <Typography className="validation-message">
                                            {methods.formState.errors.code?.message}
                                        </Typography>
                                    </Grid> */}
                  <Grid
                    item
                    md={9}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  ></Grid>
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.examinationName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Examination *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          autoFocus
                          {...methods.register("examinationName", {
                            onChange: (e) => {
                              const input = e.target;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";
                            },
                          })}
                          error={!!methods.formState.errors.examinationName}
                          className={`text-input ${
                            methods.formState.errors.examinationName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.examinationName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}
                  >
                    {curRefMemberProcedureExaminationId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
